import type {
  ArtFileDownloadDto,
  ItemAdhesive,
  ItemFinish,
  ItemLaminate,
  ItemMarketingName,
  ItemMaterial,
  ItemOrientation,
  ItemShape,
} from "@sticker-giant/sg-frontend-common";
import { create } from "zustand";
import type { ValidationErrors } from "@/lib/product/validation";
import { resolvePackagingMode } from "@/utils/resolvePackagingMode";
import type { RollDriver, RollDriverSelection } from "@/utils/rollQuantityUtils";

export interface PdpStore {
  adhesive?: ItemAdhesive;
  clearValidationErrors: () => void;
  clearValidationError: (field: string) => void;
  /**
   * Marks the one-time hydration pass (cart item / spec / URL params) as finished. Until this is
   * true the store still holds defaults, so anything that derives a request from it would be
   * asking about selections the user never made.
   */
  completeHydration: () => void;
  finish?: ItemFinish;
  height?: string;
  imageHeight?: number;
  imageWidth?: number;
  isHydrated: boolean;
  isUploadingArt: boolean;
  laminate?: ItemLaminate;
  material?: ItemMaterial;
  orientation?: ItemOrientation;
  productSku?: ItemMarketingName;
  quantity?: number;
  rollQty: number | null;
  rollCount: number | null;
  rollDriver: RollDriver;
  resetSelections: () => void;
  /**
   * Applies a *user-initiated* finish change, clearing the packaging selection in the same update
   * if the finish changes from on-rolls to individual. Hydration paths (cart item / URL params) use
   * `setFinish` so they can supply their own packaging values.
   */
  /** `defaultFinish` stands in for an unset stored finish, which the PDP displays as the product default. */
  selectFinish: (finish: ItemFinish | undefined, defaultFinish: ItemFinish | undefined) => void;
  selectProduct: (sku: ItemMarketingName) => void;
  /**
   * Applies a *user-initiated* quantity change, clearing the packaging selection in the same
   * update because it is only valid for the quantity it was resolved against. Hydration paths
   * (cart item, URL params) use `setQuantity` so they can supply their own packaging values.
   */
  selectQuantity: (quantity?: number) => void;
  /**
   * Applies a *user-initiated* size change, clearing the packaging selection in the same update
   * because it is only valid for the size it was resolved against. Hydration paths (cart item,
   * URL params) use `setWidth`/`setHeight` so they can supply their own packaging values.
   */
  selectSize: (width?: string, height?: string) => void;
  /**
   * Applies a *user-initiated* orientation change, clearing the packaging selection in the same
   * update because roll options depend on orientation. Hydration paths (cart item, URL params) and
   * `Finish` use `setOrientation`.
   */
  selectOrientation: (orientation?: ItemOrientation) => void;
  setAdhesive: (adhesive?: ItemAdhesive) => void;
  setFinish: (finish?: ItemFinish) => void;
  setHeight: (height?: string) => void;
  setImageHeight: (imageHeight?: number) => void;
  setImageWidth: (imageWidth?: number) => void;
  setIsUploadingArt: (isUploadingArt: boolean) => void;
  setLaminate: (laminate?: ItemLaminate) => void;
  setMaterial: (material?: ItemMaterial) => void;
  setOrientation: (orientation?: ItemOrientation) => void;
  setQuantity: (quantity?: number) => void;
  setRollCount: (rollCount: number | null) => void;
  setRollQty: (rollQty: number | null) => void;
  setRollDriver: (rollDriver: RollDriver) => void;
  setRollSelection: (selection: RollDriverSelection) => void;
  setShape: (shape?: ItemShape) => void;
  setUploadedFileInfo: (uploadedFileInfo?: ArtFileDownloadDto) => void;
  setSpecialInstructions: (specialInstructions?: string) => void;
  setValidationErrors: (errors: ValidationErrors) => void;
  setValidationError: (field: string, message: string) => void;
  setWidth: (width?: string) => void;
  shape?: ItemShape;
  specialInstructions?: string;
  uploadedFileInfo?: ArtFileDownloadDto;
  validationErrors: ValidationErrors;
  width?: string;
}

const resetState = {
  adhesive: undefined,
  finish: undefined,
  height: undefined,
  imageHeight: undefined,
  imageWidth: undefined,
  isUploadingArt: false,
  laminate: undefined,
  material: undefined,
  orientation: undefined,
  productSku: undefined,
  quantity: undefined,
  rollCount: null,
  rollQty: null,
  rollDriver: null,
  shape: undefined,
  specialInstructions: undefined,
  uploadedFileInfo: undefined,
  validationErrors: {} as ValidationErrors,
  width: undefined,
};

function clearedPackaging(state: PdpStore) {
  return state.rollDriver ? {} : { rollQty: null, rollCount: null };
}

export const usePdpStore = create<PdpStore>(set => ({
  ...resetState,
  isHydrated: false,
  completeHydration: () => set(state => (state.isHydrated ? state : { isHydrated: true })),
  setAdhesive: (adhesive?: ItemAdhesive) => set(() => ({ adhesive })),
  setFinish: (finish?: ItemFinish) => set(() => ({ finish })),
  setHeight: (height?: string) => set(() => ({ height })),
  setImageHeight: (imageHeight?: number) => set(() => ({ imageHeight })),
  setImageWidth: (imageWidth?: number) => set(() => ({ imageWidth })),
  setIsUploadingArt: (isUploadingArt: boolean) => set(() => ({ isUploadingArt })),
  setLaminate: (laminate?: ItemLaminate) => set(() => ({ laminate })),
  setMaterial: (material?: ItemMaterial) => set(() => ({ material })),
  setOrientation: (orientation?: ItemOrientation) => set(() => ({ orientation })),
  setQuantity: (quantity?: number) => set(() => ({ quantity })),
  setRollCount: (rollCount: number | null) => set(() => ({ rollCount })),
  setRollQty: (rollQty: number | null) => set(() => ({ rollQty })),
  setRollDriver: (rollDriver: RollDriver) => set(() => ({ rollDriver })),
  setRollSelection: ({ rollDriver, rollCount, rollQty }: RollDriverSelection) =>
    set(() => ({ rollDriver, rollCount, rollQty })),
  selectFinish: (finish: ItemFinish | undefined, defaultFinish: ItemFinish | undefined) =>
    set(state => {
      if (resolvePackagingMode(finish) === resolvePackagingMode(state.finish ?? defaultFinish)) return { finish };
      return { finish, rollQty: null, rollCount: null, rollDriver: null };
    }),
  selectProduct: (productSku: ItemMarketingName) => set(() => ({ ...resetState, productSku })),
  selectQuantity: (quantity?: number) => set(state => ({ quantity, ...clearedPackaging(state) })),
  selectSize: (width?: string, height?: string) => set(state => ({ width, height, ...clearedPackaging(state) })),
  selectOrientation: (orientation?: ItemOrientation) => set(state => ({ orientation, ...clearedPackaging(state) })),
  setShape: (shape?: ItemShape) => set(() => ({ shape })),
  setSpecialInstructions: (specialInstructions?: string) => set(() => ({ specialInstructions })),
  setUploadedFileInfo: (uploadedFileInfo?: ArtFileDownloadDto) => set(() => ({ uploadedFileInfo })),
  setValidationErrors: (validationErrors: ValidationErrors) => set(() => ({ validationErrors })),
  setValidationError: (field: string, message: string) =>
    set(state => ({ validationErrors: { ...state.validationErrors, [field]: message } })),
  setWidth: (width?: string) => set(() => ({ width })),
  clearValidationError: (field: string) =>
    set(state => {
      if (!(field in state.validationErrors)) return state;
      const { [field]: _, ...rest } = state.validationErrors;
      return { validationErrors: rest };
    }),
  clearValidationErrors: () => set(() => ({ validationErrors: {} })),
  resetSelections: () => set(() => ({ ...resetState, isHydrated: false })),
}));
